import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"
import { STATUS_LABELS } from "@/lib/project-configs"
import { ArrowRight } from "lucide-react"
import type { DomainEvent, UserSummary, PipeStatusRecord, ProjectStatus } from "@/types"

const EVENT_TYPE_LABELS: Record<string, string> = {
  ProjectCreated: "Projet créé",
  ProjectUpdated: "Projet modifié",
  ProjectDestroyed: "Projet supprimé",
  ProjectPipeStatusChanged: "Statut pipeline modifié",
  ProjectStatusTransitioned: "Transition de statut",
}

const EVENT_BADGE_VARIANT: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  ProjectCreated: "default",
  ProjectUpdated: "secondary",
  ProjectDestroyed: "destructive",
  ProjectPipeStatusChanged: "outline",
  ProjectStatusTransitioned: "outline",
}

const FIELD_LABELS: Record<string, string> = {
  name: "Nom",
  address: "Adresse",
  zip_code: "Code postal",
  city_name: "Ville",
  city_code: "Code commune",
  country: "Pays",
  email: "Email",
  phone_number: "Téléphone",
  property_type: "Type de bien",
  house_surface: "Surface bâtie",
  field_surface: "Surface terrain",
  lead_score: "Score",
  origin: "Origine",
  latitude: "Latitude",
  longitude: "Longitude",
  status: "Statut",
  pipe_status: "Suivi",
  organisation_id: "Organisation",
  created_by_id: "Créé par",
}

function formatTimestamp(iso: string): string {
  const date = new Date(iso)
  const day = date.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })
  const time = date.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })
  return `${day} à ${time}`
}

function statusLabel(value: string): string {
  return STATUS_LABELS[value as ProjectStatus] ?? value
}

function formatValue(value: unknown): string {
  if (value == null || value === "") return "—"
  return String(value)
}

function ChangedFields({ changes }: { changes: Record<string, [unknown, unknown]> }) {
  const entries = Object.entries(changes)
  if (entries.length === 0) return null

  return (
    <div className="space-y-0.5 text-sm">
      {entries.map(([field, [oldVal, newVal]]) => (
        <div key={field} className="flex flex-wrap items-center gap-1.5 text-muted-foreground">
          <span className="font-medium text-foreground">{FIELD_LABELS[field] ?? field}</span>
          <span>{formatValue(oldVal)}</span>
          <ArrowRight className="size-3" />
          <span>{formatValue(newVal)}</span>
        </div>
      ))}
    </div>
  )
}

function EventDetails({ event, pipeStatusesById }: { event: DomainEvent; pipeStatusesById: Record<string, PipeStatusRecord> }) {
  const { event_type, data } = event

  if (event_type === "ProjectPipeStatusChanged") {
    // New events store from_label/to_label; old events store from/to as strings
    const fromLabel = (data.from_label as string) || pipeStatusesById[data.from_id as string]?.label || (data.from as string) || "—"
    const toLabel = (data.to_label as string) || pipeStatusesById[data.to_id as string]?.label || (data.to as string) || "—"
    return (
      <span className="inline-flex items-center gap-1.5 text-sm">
        {fromLabel}
        <ArrowRight className="size-3 text-muted-foreground" />
        {toLabel}
      </span>
    )
  }

  if (event_type === "ProjectStatusTransitioned" && data.from && data.to) {
    return (
      <span className="inline-flex items-center gap-1.5 text-sm">
        {statusLabel(data.from as string)}
        <ArrowRight className="size-3 text-muted-foreground" />
        {statusLabel(data.to as string)}
      </span>
    )
  }

  if (event_type === "ProjectUpdated" && data.changes) {
    return <ChangedFields changes={data.changes as Record<string, [unknown, unknown]>} />
  }

  if (event_type === "ProjectCreated" && data.name) {
    return <span className="text-sm">{data.name as string}</span>
  }

  return null
}

type Props = {
  domainEvents: DomainEvent[]
  users: UserSummary[]
  pipeStatuses: PipeStatusRecord[]
}

export function HistoryTab({ domainEvents, users, pipeStatuses }: Props) {
  const usersById = new Map(users.map((u) => [u.id, u]))
  const pipeStatusesById: Record<string, PipeStatusRecord> = {}
  for (const ps of pipeStatuses) {
    pipeStatusesById[ps.id] = ps
  }

  function userName(userId: unknown): string | null {
    if (!userId) return null
    const user = usersById.get(userId as string)
    if (!user) return null
    return `${user.first_name} ${user.last_name}`
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium">Historique</h3>

      {domainEvents.length === 0 ? (
        <p className="text-sm text-muted-foreground">Aucun événement dans l'historique</p>
      ) : (
        <div className="space-y-0">
          {domainEvents.map((event, index) => (
            <div key={event.event_id}>
              <div className="flex items-start gap-3 py-3">
                <div className="mt-0.5 size-2 shrink-0 rounded-full bg-muted-foreground/40" />
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant={EVENT_BADGE_VARIANT[event.event_type] ?? "secondary"}>
                      {EVENT_TYPE_LABELS[event.event_type] ?? event.event_type}
                    </Badge>
                    <span className="text-xs text-muted-foreground">{formatTimestamp(event.timestamp)}</span>
                    {!!event.metadata?.user_id && (
                      <span className="text-xs text-muted-foreground">
                        par {userName(event.metadata.user_id) ?? "Utilisateur inconnu"}
                      </span>
                    )}
                  </div>
                  <EventDetails event={event} pipeStatusesById={pipeStatusesById} />
                </div>
              </div>
              {index < domainEvents.length - 1 && <Separator />}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
